import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Modal, Form, Row, Col } from 'react-bootstrap';
import { FiAlertTriangle, FiRefreshCw, FiEye, FiEdit, FiTrash2, FiGift, FiCheckCircle, FiCalendar, FiDownload } from 'react-icons/fi';
import { fetchClaimsRequest } from '../../store/actions/claimsActions';
import { deleteClaimById } from '../../services/api';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminClaims.css';

const EMPTY_CLAIMS_FILTERS = {
    reservation_number: '',
    policy_code: '',
    client_email: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    claim_code: '',
    date_of_claim: '',
    company_name: '',
};

const AdminClaims = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const claimsState = useSelector((state) => state.claims.list);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('activity-claims', EMPTY_CLAIMS_FILTERS, {
        persistWhen: isAuthenticated,
    });
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState(null);

    const handleDetails = (item) => {
        const claimId = item.claim_id || item.id;
        if (claimId) {
            navigate(`/activity/claims/${claimId}`, {
                state: { claim: item }
            });
        } else {
            setSelectedItem(item);
            setShowDetailModal(true);
        }
    };

    const handleDelete = (item) => {
        setSelectedItem(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!selectedItem) return;
        try {
            setDeleteError(null);
            setDeleteSuccess(null);
            const claimId = selectedItem.claim_id || selectedItem.id;
            await deleteClaimById(claimId);
            setDeleteSuccess('Sinistre supprimé avec succès !');
            setShowDeleteModal(false);
            setSelectedItem(null);
            // Recharger la liste après un court délai pour voir le message
            setTimeout(() => {
                loadClaims(currentPage);
                setDeleteSuccess(null);
            }, 1500);
        } catch (e) {
            const errorMessage = e?.response?.data?.detail || e?.message || 'Erreur lors de la suppression du sinistre';
            setDeleteError(errorMessage);
            console.error('Erreur lors de la suppression du sinistre:', e);
        }
    };

    // Charger les sinistres avec pagination et filtres
    const loadClaims = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                params[key] = activeFilters[key].trim();
            }
        });

        dispatch(fetchClaimsRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadClaims(currentPage);
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        loadClaims(currentPage);
    }, [currentPage, loadClaims]);

    // Extraire les données et métadonnées de la réponse API
    const claimsData = claimsState?.data?.data || claimsState?.data || [];
    const claimsArray = Array.isArray(claimsData) ? claimsData : [];
    const meta = claimsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + claimsArray.length, totalItems);

    const handleRefresh = () => {
        loadClaims(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadClaims(1, filters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_CLAIMS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadClaims(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les sinistres de l'API (pas de filtrage côté client)
    const currentClaims = claimsArray;

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    return (
        <div className="dashboard-page admin-claims-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiAlertTriangle className="me-2" />
                                Activities - Gestion des sinistres
                            </h2>
                            <p className="text-muted mb-0">Sinistres liés aux activités</p>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button
                                variant="primary"
                                onClick={() => navigate('/activity/claims/export', { state: { filters: { ...appliedFilters } } })}
                                className="d-flex align-items-center"
                            >
                                <FiDownload className="me-2" />
                                Export claims
                            </Button>
                            <Button
                                variant="warning"
                                onClick={() => navigate('/activity/check-claims')}
                                className="d-flex align-items-center"
                            >
                                <FiCheckCircle className="me-2" />
                                Trigger verification
                            </Button>
                        </div>
                    </div>

                    {claimsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {claimsState.error}
                        </Alert>
                    )}

                    {deleteSuccess && (
                        <Alert variant="success" className="mt-3" onClose={() => setDeleteSuccess(null)} dismissible>
                            {deleteSuccess}
                        </Alert>
                    )}

                    {deleteError && (
                        <Alert variant="danger" className="mt-3" onClose={() => setDeleteError(null)} dismissible>
                            {deleteError}
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 301826051"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_from"
                                            value={filters.activity_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_to"
                                            value={filters.activity_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code sinistre</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="claim_code"
                                            value={filters.claim_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Code du sinistre"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Partenaire (company name)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: green river"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date du sinistre</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="date_of_claim"
                                            value={filters.date_of_claim}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={claimsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${claimsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {claimsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des sinistres...</p>
                        </div>
                    ) : claimsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{claimsState.error}</Alert>
                        </div>
                    ) : currentClaims.length === 0 ? (
                        <div className="text-center py-5">
                            <FiAlertTriangle size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucun sinistre disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code sinistre</th>
                                            <th>Code police</th>
                                            <th>Numéro réservation</th>
                                            <th>Email client</th>
                                            <th>Montant</th>
                                            <th>Date sinistre</th>
                                            <th>Date fin activité</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentClaims) && currentClaims.length > 0 ? (
                                            currentClaims.map((item, idx) => (
                                                <tr key={item.claim_id || item.id || idx}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['claim_code'], 'N/A')}
                                                    </td>
                                                    <td>{getDeep(item, ['policy_info', 'policy_code'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['activity_info', 'reservation_number'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['client_info', 'email'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['amount_of_claim'], '0')}</td>
                                                    <td>{getDeep(item, ['date_of_claim'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['activity_info', 'activity_end_date'], 'N/A')}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleDetails(item)}
                                                                title="Détails"
                                                                aria-label="Détails"
                                                            >
                                                                <FiEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(item)}
                                                                title="Supprimer"
                                                                aria-label="Supprimer"
                                                            >
                                                                <FiTrash2 />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    Aucun sinistre trouvé
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} sinistre{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadClaims(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadClaims(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadClaims(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadClaims(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadClaims(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadClaims(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadClaims(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    {/* Modals */}
                    <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiEye className="me-2" />
                                Détails du sinistre
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                            {selectedItem && (
                                <div>
                                    <Card className="mb-3">
                                        <Card.Header className="bg-primary text-white">
                                            <strong>Informations principales</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Code sinistre:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['claim_code'], 'N/A')}
                                                        </span>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Code police:</strong><br />
                                                        {getDeep(selectedItem, ['policy_info', 'policy_code'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Numéro de réservation:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'reservation_number'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong><FiCalendar className="me-1" />Date du sinistre:</strong><br />
                                                        {getDeep(selectedItem, ['date_of_claim'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong><FiCheckCircle className="me-1" />Statut:</strong><br />
                                                        {getDeep(selectedItem, ['claim_status'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Date fin activité:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'activity_end_date'], 'N/A')}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-info text-white">
                                            <strong>Montants</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={4}>
                                                    <p className="mb-2">
                                                        <strong>Montant total:</strong><br />
                                                        <span className="text-primary" style={{ fontSize: '1.1rem' }}>
                                                            {getDeep(selectedItem, ['amount_of_claim'], '0')}
                                                        </span>
                                                    </p>
                                                </Col>
                                                <Col md={4}>
                                                    <p className="mb-2">
                                                        <strong><FiCheckCircle className="me-1" />Statut:</strong><br />
                                                        <span style={{ fontSize: '1.1rem' }}>
                                                            {getDeep(selectedItem, ['claim_status'], 'N/A')}
                                                        </span>
                                                    </p>
                                                </Col>
                                                <Col md={4}>
                                                    <p className="mb-2">
                                                        <strong>Étape:</strong><br />
                                                        <span style={{ fontSize: '1.1rem' }}>
                                                            {getDeep(selectedItem, ['claim_stage'], 'N/A')}
                                                        </span>
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-secondary text-white">
                                            <strong>Informations client</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Email client:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'email'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Téléphone:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'phone_number'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Adresse:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'client_address'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Nom client:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'first_name'], '')} {getDeep(selectedItem, ['client_info', 'last_name'], '')}
                                                        {!getDeep(selectedItem, ['client_info', 'first_name'], '') && !getDeep(selectedItem, ['client_info', 'last_name'], '') && 'N/A'}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Code client:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['client_info', 'client_code'], 'N/A')}
                                                        </span>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Ville/Pays:</strong><br />
                                                        {getDeep(selectedItem, ['client_info', 'city'], '')} {getDeep(selectedItem, ['client_info', 'country'], '')}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-success text-white">
                                            <strong>Informations d'activité</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Nom:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'name'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Catégorie:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'category'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Prix:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'activity_price'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Dates:</strong><br />
                                                        Du {getDeep(selectedItem, ['activity_info', 'activity_start_date'], 'N/A')} {getDeep(selectedItem, ['activity_info', 'activity_start_time'], '')}<br />
                                                        Au {getDeep(selectedItem, ['activity_info', 'activity_end_date'], 'N/A')} {getDeep(selectedItem, ['activity_info', 'activity_end_time'], '')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Adresse:</strong><br />
                                                        {getDeep(selectedItem, ['activity_info', 'activity_address'], '')}, {getDeep(selectedItem, ['activity_info', 'activity_zip_code'], '')} {getDeep(selectedItem, ['activity_info', 'activity_city'], '')}, {getDeep(selectedItem, ['activity_info', 'activity_country'], '')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Code activité:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['activity_info', 'activity_code'], 'N/A')}
                                                        </span>
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-warning text-dark">
                                            <strong>Informations de police</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Code police:</strong><br />
                                                        <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                                            {getDeep(selectedItem, ['policy_info', 'policy_code'], 'N/A')}
                                                        </span>
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Statut:</strong><br />
                                                        {getDeep(selectedItem, ['policy_info', 'status'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Prix assurance:</strong><br />
                                                        {getDeep(selectedItem, ['policy_info', 'insurance_price_excl_tsca'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Date UW:</strong><br />
                                                        {getDeep(selectedItem, ['policy_info', 'uw_date'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Langue préférée:</strong><br />
                                                        {getDeep(selectedItem, ['policy_info', 'preferred_language'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Ratio Orakle:</strong><br />
                                                        {getDeep(selectedItem, ['policy_info', 'orakle_price_ratio_excl_tsca'], 'N/A')}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-3">
                                        <Card.Header className="bg-primary text-white">
                                            <strong>Informations de déclenchement</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <Row>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Basé sur:</strong><br />
                                                        {getDeep(selectedItem, ['trigger_based_on'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Activité maintenue:</strong><br />
                                                        {getDeep(selectedItem, ['activity_maintained'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Date vérification forecast:</strong><br />
                                                        {getDeep(selectedItem, ['date_check_forecast'], 'N/A')}
                                                    </p>
                                                </Col>
                                                <Col md={6}>
                                                    <p className="mb-2">
                                                        <strong>Dernière modification:</strong><br />
                                                        {getDeep(selectedItem, ['claim_last_modified_at'], 'N/A')}
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Modifié par:</strong><br />
                                                        {getDeep(selectedItem, ['claim_last_modified_by'], 'N/A')}
                                                    </p>
                                                    {getDeep(selectedItem, ['CheckClaims', 'total_mm_prc']) && (
                                                        <p className="mb-2">
                                                            <strong>Total mm précipitation:</strong><br />
                                                            {getDeep(selectedItem, ['forecast_info', 'total_mm_prc'], 'N/A')}
                                                        </p>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Card className="mb-0">
                                        <Card.Header className="bg-dark text-white">
                                            <strong>Données complètes (JSON)</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem', maxHeight: '300px', overflowY: 'auto' }}>
                                                {JSON.stringify(selectedItem, null, 2)}
                                            </pre>
                                        </Card.Body>
                                    </Card>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                                Fermer
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={showDeleteModal} onHide={() => {
                        setShowDeleteModal(false);
                        setDeleteError(null);
                        setSelectedItem(null);
                    }}>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirmer la suppression du sinistre</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deleteError && (
                                <Alert variant="danger" className="mb-3">
                                    {deleteError}
                                </Alert>
                            )}
                            <p>Êtes-vous sûr de vouloir supprimer ce sinistre ?</p>
                            {selectedItem && (
                                <div className="mt-2">
                                    <strong>Code sinistre:</strong> {getDeep(selectedItem, ['claim_code'], 'N/A')}<br />
                                    <strong>Montant:</strong> {getDeep(selectedItem, ['amount_of_claim'], 'N/A')}
                                </div>
                            )}
                            <p className="text-danger mt-2"><small>Cette action est irréversible.</small></p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => {
                                setShowDeleteModal(false);
                                setDeleteError(null);
                                setSelectedItem(null);
                            }}>
                                Annuler
                            </Button>
                            <Button variant="danger" onClick={confirmDelete} disabled={deleteSuccess}>
                                {deleteSuccess ? 'Suppression en cours...' : 'Supprimer'}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminClaims;
